import i18n from "@/i18n.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { ApiCandidateDisqualificationReason } from "@/types/recruit/disqualification-reason.types.ts";
import { ApiJobShort } from "@/types/recruit/job.types.ts";
import { ApiSource } from "@/types/recruit/source.types.ts";
import { ApiPipelineStage } from "@/types/recruit/stages.types.ts";
import { SelectItem } from "@skailer-com/uikit";

// #todo replace with ApiAggregationDate
export enum ApiAnalyticsAggregationDate {
  day = "day",
  week = "week",
  month = "month",
  quarter = "quarter",
  year = "year",
}

export enum TotalDaysRangeType {
  lte_1_day = "lte_1_day",
  between_2_4_days = "between_2_4_days",
  between_5_7_days = "between_5_7_days",
  between_8_14_days = "between_8_14_days",
  between_15_21_days = "between_15_21_days",
  between_22_30_days = "between_22_30_days",
  gt_30_days = "gt_30_days",
}

export interface ApiAnalyticsRecruitingFilter extends ApiFilterBase {
  date_start?: Date;
  date_end?: Date;
  recruiter_ids?: number[];
  job_ids?: number[];
  aggregation_date_mode?: ApiAnalyticsAggregationDate;
  stage_ids?: number[];
  stakeholder_ids?: number[];

  group_by?: ApiAnalyticsGroupBy | null;
  department_ids?: number[];
  division_ids?: number[];

  format?: string;
  include_inactive_jobs?: boolean;
  include_all_active?: boolean;

  value_mode?: ApiAnalyticsValueMode | null;
  start_stage_id?: number | null;
  end_stage_id?: number | null;
}

export enum ApiAnalyticsGroupBy {
  owner = "owner",
  job = "job",
  none = "none",
}

export enum ApiAnalyticsValueMode {
  total = "total",
  conversion = "conversion",
  funnel = "funnel",
}

export interface ApiAnalyticsRecruitingReportRowBase {
  value: number;
  value2: number;
}

export interface ApiAnalyticsRecruitingStageFlowReportRowItem extends ApiAnalyticsRecruitingReportRowBase {
  pipeline_stage: ApiPipelineStage;
  conversion: number | null;
  avg_days: number | null;
  applicants_ids: number[];
}

export interface ApiAnalyticsRecruitingStageFlowReportRow extends ApiAnalyticsRecruitingReportRowBase {
  date_report_from: Date;
  date_report_to: Date;
  employee?: ApiEmployeeShort;
  job?: ApiJobShort;
  stages: ApiAnalyticsRecruitingStageFlowReportRowItem[];
}

export interface ApiAnalyticsRecruitingStageFlowReport {
  filter: ApiAnalyticsRecruitingFilter;
  config: {
    stages: ApiPipelineStage[];
  };
  report: {
    rows: ApiAnalyticsRecruitingStageFlowReportRow[];
  };
}

export interface ApiAnalyticsRecruitingSourceEfficiencyReportRowItem extends ApiAnalyticsRecruitingReportRowBase {
  applicants_ids: number[];
  source: ApiSource;
}

export interface ApiAnalyticsRecruitingSourceEfficiencyReportRow {
  sources: ApiAnalyticsRecruitingSourceEfficiencyReportRowItem[];
  pipeline_stage: ApiPipelineStage;
}

export interface ApiAnalyticsRecruitingSourceEfficiencyReport {
  filter: ApiAnalyticsRecruitingFilter;
  config: {
    stages: ApiPipelineStage[];
    sources: ApiSource[];
  };
  report: {
    rows: ApiAnalyticsRecruitingSourceEfficiencyReportRow[];
  };
}

export interface ApiAnalyticsDisqualificationReasonsReportRowItem extends ApiAnalyticsRecruitingReportRowBase {
  applicants_ids: number[];
  reason: ApiCandidateDisqualificationReason;
}

export interface ApiAnalyticsRecruitingDisqualificationReasonsReportRow {
  reasons: ApiAnalyticsDisqualificationReasonsReportRowItem[];
  pipeline_stage: ApiPipelineStage;
}

export interface ApiAnalyticsRecruitingDisqualificationReasonsReport {
  filter: ApiAnalyticsRecruitingFilter;
  config: {
    stages: ApiPipelineStage[];
    reasons: ApiCandidateDisqualificationReason[];
  };
  report: {
    rows: ApiAnalyticsRecruitingDisqualificationReasonsReportRow[];
  };
}

export interface ApiAnalyticsRecruitingClosingTimeReportAvgDaysRow {
  avg_days: number;
  pipeline_stage: ApiPipelineStage;
  applicants_ids: number[];
}

export interface ApiAnalyticsRecruitingClosingTimeReportTotalDaysRow {
  total_days_range: TotalDaysRangeType;
  applicants_ids: number[];
}

export interface ApiAnalyticsRecruitingClosingTimeReport {
  filter: ApiAnalyticsRecruitingFilter;
  config: {
    stages: ApiPipelineStage[];
  };
  report: {
    hired_candidate_count: number;
    hired_from_jobs_count: number;
    average_days_for_closing: number;
    hired_applicants_ids: number[];
    avg_days_rows: ApiAnalyticsRecruitingClosingTimeReportAvgDaysRow[];
    total_days_rows: ApiAnalyticsRecruitingClosingTimeReportTotalDaysRow[];
  };
}

export const getAnalyticsAggregationDateOptions = () => {
  return [
    {
      id: ApiAnalyticsAggregationDate.day,
      title: i18n.t("common:group_period_by_day"),
    },
    {
      id: ApiAnalyticsAggregationDate.week,
      title: i18n.t("common:group_period_by_week"),
    },
    {
      id: ApiAnalyticsAggregationDate.month,
      title: i18n.t("common:group_period_by_month"),
    },
    {
      id: ApiAnalyticsAggregationDate.quarter,
      title: i18n.t("common:group_period_by_quarter"),
    },
    {
      id: ApiAnalyticsAggregationDate.year,
      title: i18n.t("common:group_period_by_year"),
    },
  ] as SelectItem[];
};
